// React
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/** @jsx jsx */
import { jsx } from '@emotion/core';

// VDS-React
import Alert from '@visa/vds-react/alert-styled';
import Icon from '@visa/vds-react/icon-styled';
import AlertIcon from '@visa/vds-react/alert-icon-styled';
import AlertLabel from '@visa/vds-react/alert-label-styled';
import AlertLabelTitle from '@visa/vds-react/alert-label-title-styled';

// CYBS-UI
import CybsButton from '@cybs-ui/components/lib/button';
import CybsButtonGroup from '@cybs-ui/components/lib/button-group';
import { StructFlex } from '@cybs-ui/components/lib/struct-flex';
import styles from '../component.styles';

const InlineAlert = React.forwardRef(({ dataTest, i18n: i18nUser, isDismissable, isVisible, onDismiss, status }, ref) => {
    const i18n = {
      ...InlineAlert.defaultProps.i18n,
      ...i18nUser
    };
    const [firstLoad, setFirstLoad] = useState(true);

    ref = ref || useRef();

    useEffect(() => {
      if (isVisible && ref && ref.current && !firstLoad) {
        ref.current.focus();
      }
      setFirstLoad(false);
    }, [isVisible]);

    const titles = {
      positive: 'Positive',
      information: 'Information',
      warning: 'Warning',
      negative: 'Negative',
      success: 'Success',
      error: 'Error',
      info: 'Information',
      neutral: 'Neutral'
    };
    const imageTitle = i18n.iconTitle || titles[status] || undefined;

    return (
      isVisible && (
        <Alert
          alertType={status === 'info' ? 'information' : status}
          data-test={`${dataTest}-inline-cybsAlert`}
          position="relative"
          show={isVisible}
          customCSS={theme => styles(theme).inline}
          tabIndex={isDismissable ? -1 : undefined}
          role={isDismissable ? 'alert' : null}
          ref={ref}
        >
          <StructFlex
            direction="row"
            justify-content="space-between"
            css={theme => styles(theme).containerClass}
          >
            <AlertIcon>
              <Icon name={status} resolution="low" title={imageTitle} />
            </AlertIcon>
            <div css={theme => styles(theme).flex}>
              <AlertLabel>
                {i18n.title && (
                  <h2 style={{ color: 'black' }}>
                    <AlertLabelTitle>
                      {imageTitle && `${imageTitle}: `}
                      {i18n.title}
                    </AlertLabelTitle>
                  </h2>
                )}
                {imageTitle && !i18n.title && `${imageTitle}: `}
                {i18n.message}
              </AlertLabel>
            </div>

            {isDismissable && (
              <CybsButtonGroup
                direction="right"
                dataTest={dataTest}
                css={theme => styles(theme).closeButton}
              >
                {isVisible && (
                  <CybsButton
                    colorScheme="tertiary"
                    dataTest={dataTest}
                    iconClass="close"
                    iconType="light-tiny"
                    onClick={onDismiss}
                    i18n={{
                      ariaLabel: i18n.ariaLabel,
                      tooltipLabel: i18n.ariaLabel
                    }}
                    resolution="tiny"
                    variant="icon"
                    tooltipCustomCSS={theme => styles(theme).tooltip}
                  />
                )}
              </CybsButtonGroup>
            )}
          </StructFlex>
        </Alert>
      )
    );
  }
);

InlineAlert.propTypes = {
  /**
   * Data test prop
   * */
  dataTest: PropTypes.string.isRequired,

  /**
   * If true, the alert can be dismissed by clicking on the close button.
   */
  isDismissable: PropTypes.bool,

  /**
   * Localization dictionary
   */
  i18n: PropTypes.shape({
    /**
     * Aria Label for the close button.
     * */
    ariaLabel: PropTypes.string,
    /**
     * Message to display as the status.
     */
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    /**
     * Title to display in the alert
     */
    title: PropTypes.string,
    /**
     * Title to display in the icon
     */
    iconTitle: PropTypes.string
  }),

  /**
   * Callback fired to close the alert.
   */
  onDismiss: PropTypes.func,

  /**
   * If true, the  alert will be visible.
   */
  isVisible: PropTypes.bool,

  /**
   * @ignore
   */
  status: PropTypes.oneOf([
    'positive',
    'information',
    'warning',
    'negative',
    'success',
    'error',
    'info',
    'neutral'
  ])
};

InlineAlert.defaultProps = {
  i18n: {
    ariaLabel: 'Close alert',
    message: '',
    title: '',
    iconTitle: undefined
  },
  isDismissable: false,
  isVisible: true,
  onDismiss: null,
  status: 'information'
};

InlineAlert.displayName = 'InlineAlert';

export default InlineAlert;
